import { formatFairPayStatusAmount } from "@components/FairPayStatusShort/formatFairPayStatusAmount";
import { PERIOD_BAR_STATE } from "@components/PeriodBar/periodBarUtils";
import { PERIOD_STATUS_LABEL } from "@components/EmployeeTimeline/employeeTimelineLayout";

const PAY_PLAN_STATUS_LABELS = {
  PROPOSED: "Proposed",
  IN_FORCE: "In Force",
  TERMINATED: "Terminated",
  VOIDED: "Voided",
};

const WIRE_TO_CAMEL = {
  effective_from: "effectiveFrom",
  effective_to: "effectiveTo",
  plan_code: "planCode",
  version_number: "versionNumber",
  version_code: "versionCode",
};

/**
 * One summary `pay_plan_versions` entry in the shape this component family reads.
 * The wire is snake_case with enum statuses (`IN_FORCE`); the mocks and the
 * helpers below use camelCase with display statuses. Idempotent.
 */
export function normalizePayPlanVersion(raw) {
  if (!raw) return raw;
  const out = { ...raw, status: PAY_PLAN_STATUS_LABELS[raw.status] ?? raw.status };
  Object.entries(WIRE_TO_CAMEL).forEach(([snake, camel]) => {
    if (raw[snake] !== undefined) out[camel] = raw[snake];
  });
  return out;
}

/** Temporal state from effective dates and today (never from API). */
export function computePayPlanPeriodState(version, today) {
  if (!version || version.status === "Voided") return null;

  const todayDate = new Date(today);
  const effectiveFrom = new Date(version.effectiveFrom);
  const effectiveTo = version.effectiveTo ? new Date(version.effectiveTo) : null;

  if (effectiveTo && effectiveTo < todayDate) return "Lapsed";
  if (effectiveFrom > todayDate) return "Future";
  return "Current";
}

function firstPresentId(...candidates) {
  return candidates.find((id) => id !== null && id !== undefined && id !== "") ?? null;
}

/** Position a summary assignment sits on (camelCase or the wire's snake_case), else null. */
export function resolveAssignmentPositionId(assignment) {
  return firstPresentId(
    assignment?.positionId,
    assignment?.position_id,
    assignment?.position?.id
  );
}

/** Flags each record that has no navigation target so its bar is not offered as clickable. */
export function markNavigableRecords(records, resolveTargetId) {
  return (records || []).map((record) => ({
    ...record,
    selectable: resolveTargetId(record) !== null,
  }));
}

/** Same short amount the row shows, so the bar and the row never disagree. */
export function formatPayPlanAmountLabel(amount) {
  if (amount == null || amount === "") return "$0K";
  return formatFairPayStatusAmount(amount) || String(amount);
}

/** PeriodBar state: Proposed → draft; committed versions use computed period state. */
export function payPlanVersionToPeriodBarState(version, today) {
  if (version.status === "Proposed") return PERIOD_BAR_STATE.DRAFT;

  const periodState = computePayPlanPeriodState(version, today);
  if (periodState === "Future") return PERIOD_BAR_STATE.FUTURE;
  if (periodState === "Current") return PERIOD_BAR_STATE.CURRENT;
  return PERIOD_BAR_STATE.LAPSED;
}

export function mapPayPlanVersionsToTimelineBars(versions, today) {
  return (versions || [])
    .filter((v) => v.status !== "Voided")
    .map((v) => ({
      id: v.id,
      label: v.label || formatPayPlanAmountLabel(v.amount),
      state: payPlanVersionToPeriodBarState(v, today),
      effectiveFrom: v.effectiveFrom,
      effectiveTo: v.effectiveTo ?? null,
    }));
}

export function coversDate(record, todayDate) {
  if (!record?.effectiveFrom) return false;

  const from = new Date(record.effectiveFrom);
  if (from > todayDate) return false;

  const to = record.effectiveTo ? new Date(record.effectiveTo) : null;
  return !to || to >= todayDate;
}

/** B-18: at most one future plan — Proposed or scheduled In Force with Future period state. */
export function hasFuturePayPlan(versions, today) {
  return (versions || []).some((v) => {
    if (v.status === "Voided") return false;
    return computePayPlanPeriodState(v, today) === "Future";
  });
}

export function sortPayPlanVersionsNewestFirst(versions) {
  return [...(versions || [])].sort(
    (a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom)
  );
}

const PAY_PLAN_ROW_MENU_ACTIONS = {
  edit: {
    name: "Edit",
    value: "edit",
    icon: "icons/dropdown/rename.svg",
    destructive: false,
  },
  submit: {
    name: "Submit",
    value: "submit",
    icon: "icons/dropdown/activate.svg",
    destructive: false,
  },
  delete: {
    name: "Delete",
    value: "delete",
    icon: "icons/dropdown/trash-01.svg",
    destructive: true,
    negative: true,
  },
  resetToDraft: {
    name: "Reset to draft",
    value: "reset_to_draft",
    icon: "icons/dropdown/reverse-left.svg",
    destructive: false,
  },
  void: {
    name: "Void",
    value: "void",
    icon: "icons/dropdown/archive-new.svg",
    destructive: true,
    negative: true,
  },
  terminate: {
    name: "Terminate",
    value: "terminate",
    icon: "icons/dropdown/archive-new.svg",
    destructive: true,
    negative: true,
  },
};

/**
 * B-19 row menu items for PayPlanRowCard `menuItems` override.
 * Rule A-7: only the current In Force version can be terminated, so Terminate is
 * offered when `periodState` is "Current" (Lapsed and Future versions get Void only).
 */
export function buildPayPlanVersionMenuItems(version, periodState = null) {
  const status = version?.status;
  const approvalStatus = version?.approvalStatus || "Draft";
  const items = [];

  if (status === "Proposed") {
    if (approvalStatus === "Draft") {
      items.push(
        PAY_PLAN_ROW_MENU_ACTIONS.edit,
        PAY_PLAN_ROW_MENU_ACTIONS.submit,
        PAY_PLAN_ROW_MENU_ACTIONS.delete
      );
    } else if (approvalStatus === "Pending approval") {
      items.push(PAY_PLAN_ROW_MENU_ACTIONS.resetToDraft);
    } else if (approvalStatus === "Rejected") {
      items.push(PAY_PLAN_ROW_MENU_ACTIONS.void);
    }
  } else if (status === "In Force") {
    if (periodState === "Current") items.push(PAY_PLAN_ROW_MENU_ACTIONS.terminate);
    items.push(PAY_PLAN_ROW_MENU_ACTIONS.void);
  }

  return items;
}

export function parsePlanNumberFromCode(planCode) {
  if (planCode == null || planCode === "") return null;
  const match = String(planCode).match(/PP-(\d+)/i);
  if (match) return match[1];
  if (/^\d+$/.test(String(planCode))) return planCode;
  return null;
}

/** Map summary `pay_plan_version` records to PayPlanRowCard (ER-12066) props. */
export function mapPayPlanVersionToRowProps(version, referenceDate) {
  const periodState = computePayPlanPeriodState(version, referenceDate);

  return {
    status: version.status,
    state: periodState ? periodState.toLowerCase() : "",
    effectiveFrom: version.effectiveFrom,
    effectiveTo: version.effectiveTo ?? null,
    openEnded: !version.effectiveTo,
    amount: version.amount,
    planNumber: parsePlanNumberFromCode(version.planCode),
    versionNumber: version.versionNumber,
    versionCode: version.versionCode || "",
    targetsExistingPlan:
      version.status === "Proposed" &&
      (Boolean(version.planCode) || Boolean(version.targetsExistingPlan)),
    referenceDate,
    approvalStatus: version.approvalStatus || "Draft",
    menuItems: buildPayPlanVersionMenuItems(version, periodState),
  };
}

const RANGE_VERSION_STATES = new Set(["current", "future", "lapsed"]);

/** The summary is snake_case on the wire; mocks and older callers use camelCase. */
function pick(record, snake, camel) {
  return record?.[snake] ?? record?.[camel] ?? null;
}

function isoDay(value) {
  return value ? String(value).slice(0, 10) : null;
}

/** Whether two inclusive [from, to] day ranges share a day (null `to` = open-ended). */
function periodsOverlap(aFrom, aTo, bFrom, bTo) {
  const startsBeforeBEnds = !bTo || aFrom <= bTo;
  const endsAfterBStarts = !aTo || aTo >= bFrom;
  return startsBeforeBEnds && endsAfterBStarts;
}

/**
 * Pay range version State against today, as the API names it (Rule A-28):
 * Future before effective_from, Lapsed after the inclusive effective_to, else Current.
 * Returns lowercase, or null for a Voided version.
 */
export function computePayRangeVersionState(version, today) {
  if (!version || version.status === "Voided") return null;

  const wireState = String(version.state || "").toLowerCase();
  if (RANGE_VERSION_STATES.has(wireState)) return wireState;

  const from = isoDay(pick(version, "effective_from", "effectiveFrom"));
  const to = isoDay(pick(version, "effective_to", "effectiveTo"));
  const todayDay = isoDay(today);
  if (!from || !todayDay) return "current";
  if (from > todayDay) return "future";
  if (to && to < todayDay) return "lapsed";
  return "current";
}

/**
 * One entry per drawable pay range version: `{ version, subscription }`.
 * A version belongs to a subscription only while their periods overlap, and a
 * version shared by several subscriptions of the same range appears once, under
 * the first one it overlaps. Voided versions are never returned.
 */
export function collectPayRangeVersions(subscriptions) {
  const seen = new Set();
  const entries = [];

  (subscriptions || []).forEach((subscription) => {
    const subFrom = isoDay(pick(subscription, "effective_from", "effectiveFrom"));
    const subTo = isoDay(pick(subscription, "effective_to", "effectiveTo"));

    (subscription?.versions || []).forEach((version) => {
      if (!version || version.status === "Voided" || seen.has(version.id)) return;

      const from = isoDay(pick(version, "effective_from", "effectiveFrom"));
      const to = isoDay(pick(version, "effective_to", "effectiveTo"));
      if (subFrom && from && !periodsOverlap(from, to, subFrom, subTo)) return;

      seen.add(version.id);
      entries.push({ version, subscription });
    });
  });

  return entries;
}

export function findPayRangeVersion(subscriptions, versionId) {
  if (versionId == null) return null;
  return (
    collectPayRangeVersions(subscriptions).find(
      ({ version }) => String(version.id) === String(versionId)
    ) || null
  );
}

/** Timeline bars for the "Pay range versions" lane. */
export function mapPayRangeVersionsToTimelineBars(subscriptions, today) {
  return collectPayRangeVersions(subscriptions).map(({ version }) => ({
    id: version.id,
    label: pick(version, "version_code", "versionCode") || "",
    state: computePayRangeVersionState(version, today),
    effectiveFrom: pick(version, "effective_from", "effectiveFrom"),
    effectiveTo: pick(version, "effective_to", "effectiveTo"),
    minAmount: pick(version, "min_amount", "minAmount"),
    maxAmount: pick(version, "max_amount", "maxAmount"),
    currency: pick(version, "currency", "currency"),
    payBasis: pick(version, "pay_basis", "payBasis"),
  }));
}

const ASSIGNMENT_STATUS_LABELS = {
  draft: "Draft",
  active: "In Force",
  "in force": "In Force",
  relieved: "Relieved",
  terminated: "Terminated",
  retired: "Terminated",
  de_activated: "Terminated",
  detached: "Terminated",
};

/** "#112 - Treasury Manager" → "#112": the code alone, since the bar label already names the position. */
function shortPositionCode(positionCode) {
  if (positionCode == null || positionCode === "") return null;
  return String(positionCode).split(" - ")[0].trim() || null;
}

/**
 * Timeline bars for the Occupies Positions lane. The summary publishes assignments in
 * snake_case with no `state`; State is calculated from the dates and today, never stored.
 */
export function mapAssignmentsToTimelineBars(assignments, today) {
  return (assignments || []).map((assignment) => {
    const state = computePayRangeVersionState(assignment, today);
    const statusLabel =
      ASSIGNMENT_STATUS_LABELS[String(assignment.status || "").toLowerCase()] ?? null;
    const positionCode = shortPositionCode(pick(assignment, "position_code", "positionCode"));
    const bar = {
      id: assignment.id,
      label: pick(assignment, "position_name", "positionName") || "",
      state,
      effectiveFrom: pick(assignment, "effective_from", "effectiveFrom"),
      effectiveTo: pick(assignment, "effective_to", "effectiveTo"),
      workflowState: statusLabel ? PERIOD_STATUS_LABEL[state] : "",
      meta: { statusLabel, positionCode },
    };
    if (assignment.selectable !== undefined) bar.selectable = assignment.selectable;
    return bar;
  });
}
